/**
 * Sparziele (B-01, BE-05): ein Betrag, auf den Ole hinspart, mit Fortschritt und
 * voraussichtlichem Datum. Liegen als Zeilen `sparziel:<id>` in der
 * synchronisierten Tabelle `oberflaeche` (wie die Zaehler) - keine neue
 * Tabelle, keine Migration. Betraege in Cent.
 */
import { addMonths } from '@/lib/datetime';
import type { ID, LocalDate } from '@/domain/types';
import type { MonatsSumme } from '@/domain/finanzen/finanzen';

export interface Sparziel {
  /** `sparziel:<uuid>` */
  id: string;
  areaId: ID;
  name: string;
  zielCent: number;
  gespartCent: number;
  /** Geplante Sparrate pro Monat; ohne sie gilt der Schnitt der letzten Monate. */
  monatlichCent?: number;
  updatedAt: string;
}

export const SPARZIEL_PRAEFIX = 'sparziel:';
export const istSparzielId = (id: string): boolean => id.startsWith(SPARZIEL_PRAEFIX);

const zahl = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Systemgrenze: Zeile aus der Datenbank bzw. vom Sync pruefen. */
export function sparzielAus(roh: unknown): Sparziel | null {
  if (typeof roh !== 'object' || roh === null) return null;
  const r = roh as Record<string, unknown>;
  const ziel = zahl(r.zielCent);
  if (typeof r.id !== 'string' || !istSparzielId(r.id) || typeof r.areaId !== 'string' || typeof r.name !== 'string' || ziel === null || ziel <= 0) return null;
  const rate = zahl(r.monatlichCent);
  return {
    id: r.id, areaId: r.areaId, name: r.name, zielCent: Math.round(ziel), gespartCent: Math.max(0, Math.round(zahl(r.gespartCent) ?? 0)),
    ...(rate !== null && rate > 0 ? { monatlichCent: Math.round(rate) } : {}),
    updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : '',
  };
}

export const sparAnteil = (s: Pick<Sparziel, 'zielCent' | 'gespartCent'>): number => Math.min(1, s.gespartCent / s.zielCent);

export type PrognoseQuelle = 'plan' | 'schnitt';
export interface Prognose {
  /** `null`: nicht abzusehen (keine Sparrate) - `erreicht`: Ziel schon da. */
  datum: LocalDate | null;
  erreicht: boolean;
  monate: number | null;
  /** Rate pro Monat, mit der gerechnet wurde. */
  rateCent: number | null;
  quelle: PrognoseQuelle | null;
}

/** Durchschnittlich uebrig gebliebenes Geld der letzten (vollen) Monate, nie negativ; `null` ohne Einnahmen. */
export function sparrateAusVerlauf(verlauf: readonly MonatsSumme[]): number | null {
  const mitEinnahmen = verlauf.filter((m) => m.einnahmen > 0);
  if (!mitEinnahmen.length) return null;
  return Math.max(0, Math.round(mitEinnahmen.reduce((s, m) => s + m.saldo, 0) / mitEinnahmen.length));
}

/** Wann das Ziel erreicht ist: mit der geplanten Rate, sonst mit dem Schnitt der letzten Monate. */
export function prognose(s: Sparziel, heute: LocalDate, schnittRateCent: number | null): Prognose {
  if (s.gespartCent >= s.zielCent) return { datum: null, erreicht: true, monate: 0, rateCent: null, quelle: null };
  const rate = s.monatlichCent ?? schnittRateCent;
  if (!rate || rate <= 0) return { datum: null, erreicht: false, monate: null, rateCent: null, quelle: null };
  const monate = Math.ceil((s.zielCent - s.gespartCent) / rate);
  return { datum: addMonths(heute, monate), erreicht: false, monate, rateCent: rate, quelle: s.monatlichCent ? 'plan' : 'schnitt' };
}
